import {
  IMPACT_REPORT_ABOUT_LABEL,
  IMPACT_REPORT_ABOUT_TEXT,
  IMPACT_REPORT_CERTIFICATION_BADGES,
  IMPACT_REPORT_PHOTO_CREDITS_LABEL,
} from "@/constants/impactReport";
import type { ReportData } from "@/interfaces/impactReport";

interface ImpactReportColophonProps {
  /**
   * `data.thankYou?.photoCredits` verbatim — `undefined` only when `thankYou` itself is absent
   * from the payload. The stub ships `thankYou` with a non-empty `photoCredits`, so this prop
   * renders real credits against it; `undefined` remains a live state against a payload where
   * `thankYou` is genuinely missing.
   *
   * Printed exactly as handed over: this component never drops, reorders or renumbers a credit.
   * Reconciling the wire's page numbers with a document whose first page was suppressed happens
   * before this point, in `resolveImpactReportPhotoCredits`.
   */
  photoCredits: NonNullable<ReportData["thankYou"]>["photoCredits"] | undefined;
  /**
   * The badges to draw, in order. Defaults to the constant every on-screen render uses; the PDF
   * serialiser passes the same badges with each `src` swapped for a `data:` URI, since the
   * converter has no route to the CDN these are hosted on. Typed as the constant itself so a badge
   * added there needs no edit here.
   */
  certificationBadges?: typeof IMPACT_REPORT_CERTIFICATION_BADGES;
}

/**
 * The colophon: photo credits (left) + About Gifts for Good (right) + certification badges,
 * mirroring the masthead's layout below the document.
 *
 * Ported from the prototype's `irRenderFooter()` / footer markup (client-experience-prototype.
 * html, ~5684–5700, ~8391–8394), with one deliberate omission: `irvPhotoCredit()`, which derives
 * an attribution by parsing the backdrop image's filename against a local lookup table, is not
 * ported. That derivation is exactly the Platform-side authoring this slice exists to remove —
 * credits come from the wire or they do not print.
 */
export function ImpactReportColophon({
  photoCredits,
  certificationBadges = IMPACT_REPORT_CERTIFICATION_BADGES,
}: ImpactReportColophonProps) {
  const hasCredits = photoCredits !== undefined && photoCredits.length > 0;

  return (
    <div className="irv-footer irv-footer--report">
      {hasCredits && (
        <div className="irv-footer-col">
          <div className="irv-footer-label">{IMPACT_REPORT_PHOTO_CREDITS_LABEL}</div>
          <p className="irv-footer-text">
            {photoCredits.map((credit) => `Page ${credit.page}, ${credit.attribution}`).join("; ")}
          </p>
        </div>
      )}
      <div className="irv-footer-col irv-footer-about">
        <div className="irv-footer-label">{IMPACT_REPORT_ABOUT_LABEL}</div>
        <p className="irv-footer-text">{IMPACT_REPORT_ABOUT_TEXT}</p>
      </div>
      <div className="irv-footer-certs">
        {certificationBadges.map((badge) => (
          <img key={badge.alt} className="irv-cert" src={badge.src} alt={badge.alt} />
        ))}
      </div>
    </div>
  );
}
